/**
 * Homepage Product Carousel — multi-instance scroll controller.
 *
 * The theme bundle's moduleB() uses document.querySelector('.hp-module-b'), so
 * only the first carousel on the page gets its handlers. This initializes all
 * of them.
 */
(() => {
  // Every rendered section emits this script tag, so it may execute more than once.
  if (window.__hpProductCarouselBound) return;
  window.__hpProductCarouselBound = true;

  const SECTION = '.hp-module-b';
  const SCROLL_STEP = 600;
  const DRAG_THRESHOLD = 12;

  const initCarousel = (section) => {
    const track = section.querySelector('.drag-to-scroll');
    if (!track) return;

    section._hpAbort?.abort();
    const { signal } = (section._hpAbort = new AbortController());

    // Replacing the arrows sheds the bundle's handlers. The track must never be
    // replaced: ORW.lazyload observes the img.lazy nodes inside it, and clones
    // would stay stuck on their placeholder.
    const buttons = section.querySelector('.scroll-buttons');
    const swap = (node) => {
      if (!node) return null;
      const clone = node.cloneNode(true);
      node.parentNode.replaceChild(clone, node);
      return clone;
    };
    const left = swap(buttons?.querySelector('.scroll-left'));
    const right = swap(buttons?.querySelector('.scroll-right'));

    const syncLimits = () => {
      if (!left || !right) return;
      // Read the overflow live: it is 0 while images are still loading.
      const max = track.scrollWidth - track.offsetWidth;
      if (max <= 0) {
        left.classList.add('limit');
        right.classList.add('limit');
        return;
      }
      left.classList.toggle('limit', track.scrollLeft <= 0);
      right.classList.toggle('limit', track.scrollLeft >= max - 1);
    };

    if (left && right) {
      left.addEventListener('click', () => {
        track.scrollLeft -= SCROLL_STEP;
        requestAnimationFrame(syncLimits);
      }, { signal });
      right.addEventListener('click', () => {
        track.scrollLeft += SCROLL_STEP;
        requestAnimationFrame(syncLimits);
      }, { signal });
    }

    let isDown = false;
    let startX = 0;
    let startScroll = 0;

    track.addEventListener('mousedown', (event) => {
      isDown = true;
      startX = event.pageX - track.offsetLeft;
      startScroll = track.scrollLeft;
    }, { signal });
    ['mouseleave', 'mouseup'].forEach((type) =>
      track.addEventListener(type, () => {
        isDown = false;
        track.classList.remove('active');
      }, { signal })
    );
    track.addEventListener('mousemove', (event) => {
      if (!isDown) return;
      event.preventDefault();
      const walk = event.pageX - track.offsetLeft - startX;
      track.scrollLeft = startScroll - walk;
      if (walk > DRAG_THRESHOLD || walk < -DRAG_THRESHOLD) {
        track.classList.add('active');
      }
    }, { signal });

    track.addEventListener('scroll', () => requestAnimationFrame(syncLimits), {
      passive: true,
      signal,
    });
    window.addEventListener('resize', syncLimits, { signal });

    syncLimits();
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', syncLimits, { once: true, signal });
      }
    });
  };

  const initAll = (root = document) => {
    const sections =
      root instanceof Element && root.matches(SECTION)
        ? [root]
        : root.querySelectorAll(SECTION);
    sections.forEach(initCarousel);
    window.ORW?.lazyload?.update?.();
  };

  // theme.js is deferred at the end of <body>, so waiting for `load` puts our
  // pass after moduleB() instead of before it.
  if (document.readyState === 'complete') {
    initAll();
  } else {
    window.addEventListener('load', () => initAll(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
